import { useState, useCallback } from 'react';
import * as strategyApi from '../api/strategy';
import { invalidateStrategyGuidance } from './useStrategyGuidance';
import type {
  StrategyTemplate,
  ActiveStrategyDto,
  ActiveStrategyReference,
  CreateStrategyRequest,
  UpdateStrategyRequest,
} from '../types/strategy';

interface UseStrategyTemplatesResult {
  templates: StrategyTemplate[];
  activeStrategyRef: ActiveStrategyReference | null;
  activeStrategy: ActiveStrategyDto | null;
  isLoading: boolean;
  error: string | null;
  loadTemplates: () => Promise<void>;
  loadActiveStrategy: () => Promise<void>;
  createTemplate: (request: CreateStrategyRequest) => Promise<StrategyTemplate>;
  updateTemplate: (strategyId: string, request: UpdateStrategyRequest) => Promise<StrategyTemplate>;
  deleteTemplate: (strategyId: string) => Promise<void>;
  setActiveStrategyCustom: (strategyId: string) => Promise<ActiveStrategyDto>;
  setActiveStrategyPremade: (premadeKey: string) => Promise<ActiveStrategyDto>;
  deactivateStrategy: () => Promise<void>;
}

export function useStrategyTemplates(worldId: string | null, provinceId: string | null): UseStrategyTemplatesResult {
  const [templates, setTemplates] = useState<StrategyTemplate[]>([]);
  const [activeStrategyRef, setActiveStrategyRef] = useState<ActiveStrategyReference | null>(null);
  const [activeStrategy, setActiveStrategy] = useState<ActiveStrategyDto | null>(null);
  // Initializes `true` (not `false`) — Strategy.tsx's "load active strategy into the form on
  // mount" effect uses `!templatesLoading` as its "the fetch has genuinely settled" signal. A
  // `false` initial value is indistinguishable from "confirmed: fetch complete", so on the very
  // first render (before loadTemplates() has even been called) that effect would prematurely mark
  // itself done via its stale-closure read of this flag, permanently skipping the real
  // activeStrategyRef-driven load once the fetch actually resolves — the form silently stays on
  // default/empty values while the template dropdown (fed directly by templates/activeStrategyRef,
  // not gated on this ref) still shows the correct selection. Mirrors the identical SF-172 fix
  // already applied to useStrategyGuidance.ts for the same reason.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    if (!worldId || !provinceId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await strategyApi.listTemplates(worldId, provinceId);
      setTemplates(response.templates);
      setActiveStrategyRef(response.activeStrategy);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [worldId, provinceId]);

  const loadActiveStrategy = useCallback(async () => {
    if (!worldId || !provinceId) return;
    try {
      const strategy = await strategyApi.getActiveStrategy(worldId, provinceId);
      setActiveStrategy(strategy);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load active strategy');
    }
  }, [worldId, provinceId]);

  const createTemplate = useCallback(async (request: CreateStrategyRequest): Promise<StrategyTemplate> => {
    if (!worldId || !provinceId) throw new Error('Not authenticated');
    const template = await strategyApi.createTemplate(worldId, provinceId, request);
    await loadTemplates();
    return template;
  }, [worldId, provinceId, loadTemplates]);

  const updateTemplate = useCallback(async (strategyId: string, request: UpdateStrategyRequest): Promise<StrategyTemplate> => {
    if (!worldId || !provinceId) throw new Error('Not authenticated');
    const template = await strategyApi.updateTemplate(worldId, provinceId, strategyId, request);
    invalidateStrategyGuidance(worldId, provinceId);
    await loadTemplates();
    return template;
  }, [worldId, provinceId, loadTemplates]);

  const deleteTemplate = useCallback(async (strategyId: string): Promise<void> => {
    if (!worldId || !provinceId) throw new Error('Not authenticated');
    await strategyApi.deleteTemplate(worldId, provinceId, strategyId);
    invalidateStrategyGuidance(worldId, provinceId);
    await loadTemplates();
    await loadActiveStrategy();
  }, [worldId, provinceId, loadTemplates, loadActiveStrategy]);

  const setActiveStrategyCustom = useCallback(async (strategyId: string): Promise<ActiveStrategyDto> => {
    if (!worldId || !provinceId) throw new Error('Not authenticated');
    const strategy = await strategyApi.setActiveStrategy(worldId, provinceId, { strategyId, premadeKey: null });
    invalidateStrategyGuidance(worldId, provinceId);
    setActiveStrategy(strategy);
    await loadTemplates();
    return strategy;
  }, [worldId, provinceId, loadTemplates]);

  const setActiveStrategyPremade = useCallback(async (premadeKey: string): Promise<ActiveStrategyDto> => {
    if (!worldId || !provinceId) throw new Error('Not authenticated');
    const strategy = await strategyApi.setActiveStrategy(worldId, provinceId, { strategyId: null, premadeKey });
    invalidateStrategyGuidance(worldId, provinceId);
    setActiveStrategy(strategy);
    await loadTemplates();
    return strategy;
  }, [worldId, provinceId, loadTemplates]);

  const deactivateStrategy = useCallback(async (): Promise<void> => {
    if (!worldId || !provinceId) throw new Error('Not authenticated');
    await strategyApi.deactivateStrategy(worldId, provinceId);
    invalidateStrategyGuidance(worldId, provinceId);
    setActiveStrategy(null);
    setActiveStrategyRef(null);
    await loadTemplates();
  }, [worldId, provinceId, loadTemplates]);

  return {
    templates, activeStrategyRef, activeStrategy, isLoading, error,
    loadTemplates, loadActiveStrategy,
    createTemplate, updateTemplate, deleteTemplate,
    setActiveStrategyCustom, setActiveStrategyPremade, deactivateStrategy,
  };
}
